import React, { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import {
  GROUPS,
  METRICS,
  SAMPLE_COST_BY_AGENT,
  SAMPLE_STRATEGIC,
  SAMPLE_WEEKLY,
  headlineMetrics,
  metricsByGroup,
} from './metrics/catalog'
import { MetricInfoButton, MetricInfoDrawer } from './metrics/MetricInfoDrawer'
import type { MetricDef, MetricGroup, WireStatus } from './metrics/types'

type TabId = 'headline' | MetricGroup | 'wire'

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'headline', label: 'Agency pilot' },
  ...GROUPS.map((g) => ({ id: g.id as TabId, label: g.label })),
  { id: 'wire', label: 'How to wire' },
]

function formatMetric(m: MetricDef): string {
  const v = m.sample
  if (v == null || !Number.isFinite(v)) return '—'
  switch (m.unit) {
    case 'percent':
      return `${Math.round(v * 100)}%`
    case 'usd':
      if (v < 1) return `$${v.toFixed(2)}`
      return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    case 'duration_ms': {
      const s = v / 1000
      if (s < 90) return `${Math.round(s)}s`
      const mnt = s / 60
      if (mnt < 90) return `${mnt < 10 ? mnt.toFixed(1) : Math.round(mnt)}m`
      const h = mnt / 60
      if (h < 48) return `${h < 10 ? h.toFixed(1) : Math.round(h)}h`
      return `${(h / 24).toFixed(1)}d`
    }
    case 'ratio':
    case 'per_fte':
      return v % 1 === 0 ? String(v) : v.toFixed(1)
    default:
      return v.toLocaleString()
  }
}

function wireBadge(status: WireStatus) {
  if (status === 'existing') {
    return <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Existing tables</span>
  }
  if (status === 'proxy') {
    return <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Proxy today</span>
  }
  return <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">Needs event</span>
}

function Kpi({ m, onInfo }: { m: MetricDef; onInfo: (m: MetricDef) => void }) {
  return (
    <div className="relative bg-white border border-gray-200 rounded-xl p-4 shadow-sm min-h-[7.5rem] flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide leading-snug pr-1">{m.name}</p>
        <MetricInfoButton metric={m} onOpen={onInfo} />
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-1.5 tabular-nums">{formatMetric(m)}</p>
      <p className="text-xs text-gray-500 mt-auto pt-2 leading-snug">{m.tellsYou}</p>
    </div>
  )
}

function MetricRow({ m, onInfo }: { m: MetricDef; onInfo: (m: MetricDef) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex items-start">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
        >
        {open ? (
          <ChevronDownIcon className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{m.name}</span>
            {wireBadge(m.wire)}
            {m.headline && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">Headline</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{m.tellsYou}</p>
        </div>
        <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">{formatMetric(m)}</span>
      </button>
      <div className="pr-4 py-3 shrink-0">
        <MetricInfoButton metric={m} onOpen={onInfo} />
      </div>
    </div>
      {open && (
        <div className="px-11 pb-4 text-sm text-gray-700 space-y-3">
          <p><span className="font-medium text-gray-900">Formula. </span>{m.formula}</p>
          <p><span className="font-medium text-gray-900">Grain. </span>{m.grain}</p>
          <p><span className="font-medium text-gray-900">How to calculate. </span>{m.calculate}</p>
          <div>
            <p className="font-medium text-gray-900 mb-1">Motionode sources</p>
            <ul className="list-disc pl-5 text-xs font-mono text-gray-600 space-y-0.5">
              {m.sources.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
          {m.gaps && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <span className="font-medium">Gap. </span>{m.gaps}
            </p>
          )}
          <p className="text-[11px] text-gray-400">Stable id for the future API: <code className="font-mono">{m.id}</code></p>
          <button
            type="button"
            onClick={() => onInfo(m)}
            className="text-xs font-medium text-indigo-700 hover:text-indigo-900"
          >
            Open backend recipe →
          </button>
        </div>
      )}
    </div>
  )
}

const Metrics: React.FC = () => {
  const [tab, setTab] = useState<TabId>('headline')
  const [range, setRange] = useState('30d')
  const [query, setQuery] = useState('')
  const [infoMetric, setInfoMetric] = useState<MetricDef | null>(null)

  const external = useMemo(() => headlineMetrics().filter((m) => m.side === 'external'), [])
  const internal = useMemo(() => headlineMetrics().filter((m) => m.side === 'internal'), [])

  const group = tab !== 'headline' && tab !== 'wire' ? tab : null
  const groupMeta = GROUPS.find((g) => g.id === group)
  const rows = useMemo(() => {
    const list = group ? metricsByGroup(group) : METRICS
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter((m) =>
      `${m.name} ${m.id} ${m.tellsYou} ${m.sources.join(' ')}`.toLowerCase().includes(q),
    )
  }, [group, query])

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Metrics</h1>
          <p className="text-sm text-gray-500 mt-0.5 max-w-3xl">
            Agency-pilot proof: clients get a better service, the agency coordinates less,
            engineers produce more per person. Numbers on this page are hardcoded samples.
            Each metric already names the Motionode tables to wire.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <select
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <span className="font-semibold">Sample data.</span> These figures are not live.
        Do not put them on the website. When the backend exists, keep the metric <code className="font-mono text-xs">id</code>s
        and replace the sample map — grain is always one client outcome (<code className="font-mono text-xs">objective_id</code> / request), not an agent run.
      </div>

      <div className="border-b border-gray-200 flex gap-5 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`pb-2.5 -mb-px border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'headline' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">External / service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {external.map((m) => <Kpi key={m.id} m={m} onInfo={setInfoMetric} />)}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Internal / operation</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {internal.map((m) => <Kpi key={m.id} m={m} onInfo={setInfoMetric} />)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-base font-semibold text-gray-900">Accepted outcomes vs human hours</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">Sample weekly series · source later: request completions + assignee real_time</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={SAMPLE_WEEKLY}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="t" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="outcomes" name="Accepted outcomes" stroke="#4f46e5" strokeWidth={2} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="hours" name="Human hours" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-base font-semibold text-gray-900">The three headlines</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">Sample before → after. Not real Advante results.</p>
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="text-gray-500">Active systems / engineer</dt>
                  <dd className="text-gray-900 font-semibold tabular-nums">{SAMPLE_STRATEGIC.systemsPerEngineerBefore} → {SAMPLE_STRATEGIC.systemsPerEngineerAfter}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Human hours / accepted outcome</dt>
                  <dd className="text-gray-900 font-semibold tabular-nums">{SAMPLE_STRATEGIC.hoursPerOutcomeBefore}h → {SAMPLE_STRATEGIC.hoursPerOutcomeAfter}h</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Outcomes / delivery FTE (30d)</dt>
                  <dd className="text-gray-900 font-semibold tabular-nums">{SAMPLE_STRATEGIC.outcomesPerFteBefore} → {SAMPLE_STRATEGIC.outcomesPerFteAfter}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900">AI cost by agent (sample)</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-4">Later: SUM(data_ai_agent_runs.usd_cost) GROUP BY agent_key · not credits</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={SAMPLE_COST_BY_AGENT}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="agent" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => [`$${Number(v).toFixed(1)}`, 'USD']} />
                  <Bar dataKey="usd" name="USD" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {group && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{groupMeta?.label}</h2>
            <p className="text-sm text-gray-500">{groupMeta?.blurb}</p>
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter metrics, ids, or tables…"
            className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {rows.map((m) => <MetricRow key={m.id} m={m} onInfo={setInfoMetric} />)}
            {rows.length === 0 && (
              <p className="px-4 py-8 text-sm text-gray-500">No metrics match.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'wire' && (
        <div className="space-y-6 text-sm text-gray-700">
          <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <InformationCircleIcon className="w-5 h-5 text-indigo-600" />
              Analytics root
            </h2>
            <p>
              One client request is one outcome. A Detective run, a research delegate, a Builder retry, and two QA attempts are children, not eight successes.
              Parent key: <code className="font-mono text-xs">data_mo_objective_memories.objective_id</code> when Mo is involved, else <code className="font-mono text-xs">data_requests.uuid</code>.
              Join tickets via <code className="font-mono text-xs">request_id</code>, Mo via <code className="font-mono text-xs">primary_ticket_uuid</code> / <code className="font-mono text-xs">data_mo_thread_entities</code>.
            </p>
            <p>
              Prefer rolling cost and agent counts from <code className="font-mono text-xs">data_ai_agent_runs</code> (already the cross-surface rollup: Mo, collaborators, Builder).
              Do not invent a parallel analytics warehouse until this table cannot answer the question.
            </p>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h2 className="text-base font-semibold text-gray-900">Human intervention</h2>
            <p>
              <span className="font-medium">Mandatory control</span> — a production approval, client preview approve, or billing PROPOSED→APPROVED that you intentionally require.
              These do not disqualify “AI implemented autonomously.”
            </p>
            <p>
              <span className="font-medium">Human rescue</span> — AI was supposed to do the work and failed: <code className="font-mono text-xs">continuationIntent=escalate_human</code>,
              Builder <code className="font-mono text-xs">needs_revision</code>, developer coding after Builder,
              or founder-rescue (OWNER/TEAM_LEADER acting after <code className="font-mono text-xs">status=error</code>).
            </p>
            <p>
              Track autonomy as layers, not one percentage: investigation, planning, organization, implementation, verification, end-to-end.
            </p>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h2 className="text-base font-semibold text-gray-900">Reuse these streams</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="font-mono text-xs">data_ai_agent_runs</code> + <code className="font-mono text-xs">data_ai_model_calls</code> + <code className="font-mono text-xs">data_ai_run_feedback</code> — cost, retries, technical vs task outcome</li>
              <li><code className="font-mono text-xs">data_mo_conversation_messages</code> — client questions, first response</li>
              <li><code className="font-mono text-xs">data_mo_objective_memories.memory_json</code> — blockedOnUser, continuationIntent, remaining work</li>
              <li><code className="font-mono text-xs">data_ticket_user_assignees</code> — wait on client vs agency, handoff latency, human hours</li>
              <li><code className="font-mono text-xs">data_project_activity_events</code> — CHANGE_STARTED → PREVIEW_READY → CHANGE_APPROVED → CHANGE_RELEASED</li>
              <li><code className="font-mono text-xs">data_ai_verification_runs</code> — first-pass QA, unable_to_reproduce</li>
              <li><code className="font-mono text-xs">data_ai_preview_instances</code> — generate success; opens still need an event</li>
              <li><code className="font-mono text-xs">data_assignation_logs</code> / queues — auto-routed work</li>
            </ul>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h2 className="text-base font-semibold text-gray-900">Events to add only if missing</h2>
            <p>
              Do not infer from chat text. Emit structured events when the lifecycle already mutates.
              Suggested names; skip any that already exist as tables above.
            </p>
            <p className="font-mono text-xs leading-relaxed text-gray-600">
              preview_opened · ticket_structurally_edited · human_code_change · ai_code_change ·
              human_rescue · cancel_requested · cancel_completed · environment_mismatch ·
              post_release_defect · rollback
            </p>
            <p>
              Future API: <code className="font-mono text-xs">GET /admin/metrics?range=30d</code> returning
              {' '}
              <code className="font-mono text-xs">{'{ sample, metrics: { [id]: { value, series } } }'}</code>
              {' '}
              using the ids on each row. {METRICS.length} metrics are catalogued.
            </p>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Full catalog</h2>
              <p className="text-xs text-gray-500 mt-0.5">Expand any row in the group tabs for the calculation. Search here by table or id.</p>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter…"
                className="mt-3 w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            {(query.trim() ? METRICS.filter((m) => `${m.name} ${m.id} ${m.sources.join(' ')}`.toLowerCase().includes(query.trim().toLowerCase())) : METRICS)
              .map((m) => <MetricRow key={m.id} m={m} onInfo={setInfoMetric} />)}
          </section>
        </div>
      )}

      {infoMetric && (
        <MetricInfoDrawer metric={infoMetric} onClose={() => setInfoMetric(null)} />
      )}
    </div>
  )
}

export default Metrics
